import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Box,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';
import { useIsAdmin } from '../../../contexts/PermissionsContext';


const LeaguesManagePage = () => {
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = useIsAdmin();

  const fetchLeagues = useCallback(async () => {
    try {
      const basicLeagues = await unifiedApi.getLeagues();
      
      // Fetch stats for each league to get team and member counts
      const leaguesWithStats = await Promise.all(
        (basicLeagues || []).map(async (league) => {
          try {
            const stats = await unifiedApi.getLeagueStats(league.id);
            return {
              ...league,
              teamCount: stats.totalTeams,
              memberCount: stats.totalUsers,
              isActive: stats.isActive
            };
          } catch (err) {
            console.error(`Failed to fetch stats for league ${league.id}`, err);
            return {
              ...league,
              teamCount: league.teamCount || 0,
              memberCount: league.memberCount || 0
            };
          }
        })
      );
      
      setLeagues(leaguesWithStats || []);
    } catch (err) {
      console.error('Failed to fetch leagues', err);
    }
  }, []);

  useEffect(() => {
    fetchLeagues().finally(() => setLoading(false));
  }, [fetchLeagues]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  };

  const sortedLeagues = [...leagues].sort((a, b) => {
    const aDate = a.startDate ? new Date(a.startDate) : new Date(0);
    const bDate = b.startDate ? new Date(b.startDate) : new Date(0);
    return bDate - aDate;
  });

  // Add window focus event to refresh data when returning from edit
  useEffect(() => {
    const handleFocus = () => {
      fetchLeagues();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchLeagues]);

  if (loading) {
    return (
      <AdminLayout>
        <Typography>Loading leagues...</Typography>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Manage Leagues
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isAdmin && (
          <Button 
            variant="contained" 
            component={Link}
            to={`/admin/leagues/create`}
          >
            Create New League
          </Button>
          )}
        </Box>
      </Box>

      <Paper sx={{ width: '100%' }}>
        {/* Desktop Table View */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>League Name</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell align="center">Teams</TableCell>
                <TableCell align="center">Members</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedLeagues.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No leagues found. Create your first league to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedLeagues.map((league) => (
                  <TableRow key={league.id} hover>
                    <TableCell>
                      <Typography 
                        variant="body1" 
                        fontWeight="medium"
                        component={Link}
                        to={`/admin/leagues/${league.id}`}
                        sx={{ 
                          textDecoration: 'none',
                          color: 'primary.main',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        {league.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(league.startDate)}</TableCell>
                    <TableCell>{formatDate(league.endDate)}</TableCell>
                    <TableCell align="center">{league.teamCount}</TableCell>
                    <TableCell align="center">{league.memberCount}</TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={league.isActive ? 'Active' : 'Archived'}
                        color={league.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        <Tooltip title="League Admin Dashboard">
                          <Button
                            size="small"
                            variant="contained"
                            component={Link}
                            to={`/admin/leagues/${league.id}`}
                          >
                            Admin
                          </Button>
                        </Tooltip>
                        <Tooltip title="View League Members">
                          <IconButton
                            size="small"
                            component={Link}
                            to={`/admin/leagues/${league.id}/members`}
                          >
                            <PeopleIcon />
                          </IconButton>
                        </Tooltip>
                        {isAdmin && (
                        <Tooltip title="League Settings">
                          <IconButton
                            size="small"
                            component={Link}
                            to={`/admin/leagues/${league.id}/settings`}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>

        {/* Mobile Card View */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, p: 2 }}>
          {sortedLeagues.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center">
              No leagues found. Create your first league to get started.
            </Typography>
          ) : (
            sortedLeagues.map((league) => (
              <Paper 
                key={league.id} 
                elevation={1} 
                sx={{ 
                  p: 2, 
                  mb: 2, 
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Typography 
                  variant="h6" 
                  component={Link}
                  to={`/admin/leagues/${league.id}`}
                  sx={{ 
                    textDecoration: 'none',
                    color: 'primary.main',
                    display: 'block',
                    mb: 2,
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {league.name}
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">Season:</Typography>
                    <Typography variant="body2" fontWeight="medium">{formatDate(league.startDate)} – {formatDate(league.endDate)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">Teams:</Typography>
                    <Typography variant="body2" fontWeight="medium">{league.teamCount}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">Members:</Typography>
                    <Typography variant="body2" fontWeight="medium">{league.memberCount}</Typography>
                  </Box>
                  <Chip 
                    label={league.isActive ? 'Active' : 'Archived'}
                    color={league.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    component={Link}
                    to={`/admin/leagues/${league.id}`}
                  >
                    Admin
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    component={Link}
                    to={`/admin/leagues/${league.id}/members`}
                    startIcon={<PeopleIcon />}
                  >
                    Members
                  </Button>
                  {isAdmin && (
                  <Button
                    size="small"
                    variant="outlined"
                    component={Link}
                    to={`/admin/leagues/${league.id}/settings`}
                    startIcon={<EditIcon />}
                  >
                    Settings
                  </Button>
                  )}
                </Box>
              </Paper>
            ))
          )}
        </Box>
      </Paper>
    </AdminLayout>
  );
};

export default LeaguesManagePage;
